import React from "react";
import { defineMessage, FormattedMessage, MessageDescriptor } from "react-intl";

const contentMessages: Record<string, MessageDescriptor> = {
    "section.fields": defineMessage({ defaultMessage: "Fields" }),
    "section.documents": defineMessage({ defaultMessage: "Documents" }),
    "section.signCodeOfConduct": defineMessage({ defaultMessage: "Sign Code of Conduct" }),
    "field.headquartersCountry": defineMessage({ defaultMessage: "Headquarters Country" }),
    "field.geographicalExposure": defineMessage({ defaultMessage: "Geographical exposure" }),
    "field.nace": defineMessage({ defaultMessage: "NACE" }),
    "field.legalName": defineMessage({ defaultMessage: "Legal name" }),
    "field.registrationNumber": defineMessage({ defaultMessage: "Organization number" }),
    "field.website": defineMessage({ defaultMessage: "Website" }),
    "question.document.description": defineMessage({
        defaultMessage: "Please upload and fill in the document's expiration date (if applicable)",
    }),
    "questionnaire.informationRequest": defineMessage({ defaultMessage: "Information Request" }),
    "docType.codeOfConduct": defineMessage({ defaultMessage: "Code of Conduct" }),
    "docType.annualReport": defineMessage({ defaultMessage: "Annual Report" }),
    "docType.esgReport": defineMessage({ defaultMessage: "ESG Report" }),
    "docType.other": defineMessage({ defaultMessage: "Other" }),
    "dimension.quality": defineMessage({ defaultMessage: "Quality" }),
    "dimension.commercial": defineMessage({ defaultMessage: "Commercial" }),
    "dimension.delivery": defineMessage({ defaultMessage: "Delivery" }),
    "dimension.collaboration": defineMessage({ defaultMessage: "Collaboration" }),
    "dimension.hse": defineMessage({ defaultMessage: "HSE (Health, Safety, Environment)" }),
    "dimension.sustainability": defineMessage({ defaultMessage: "Sustainability" }),
    "dimension.innovation": defineMessage({ defaultMessage: "Innovation and Improvements" }),
    "dimension.quality.description": defineMessage({
        defaultMessage: "Product or service quality and ability to meet agreed standards.",
    }),
    "dimension.commercial.description": defineMessage({
        defaultMessage: "Pricing competitiveness, cost stability, and contract adherence.",
    }),
    "dimension.delivery.description": defineMessage({
        defaultMessage: "Reliability in delivering on time and in full.",
    }),
    "dimension.collaboration.description": defineMessage({
        defaultMessage: "Communication, responsiveness, and ability to work well together.",
    }),
    "dimension.hse.description": defineMessage({
        defaultMessage: "Operational safety, incident history, and audit performance.",
    }),
    "dimension.sustainability.description": defineMessage({
        defaultMessage: "Environmental impact, emissions, certifications, and initiatives.",
    }),
    "dimension.innovation.description": defineMessage({
        defaultMessage: "Willingness to improve, co-develop, and adopt new technology.",
    }),
};

const contentDescriptions: Record<string, MessageDescriptor> = {
    "field.headquartersCountry": defineMessage({
        defaultMessage: "The country where your company is registered and has its head office.",
    }),
    "field.geographicalExposure": defineMessage({
        defaultMessage: "The countries or regions where your company has significant operations or business activity.",
    }),
    "field.nace": defineMessage({
        defaultMessage: "The standard industry classification code that best matches your company's main activity.",
    }),
    "field.legalName": defineMessage({
        defaultMessage: "Your company's official legal name.",
    }),
    "field.registrationNumber": defineMessage({
        defaultMessage: "Your company's organization number.",
    }),
    "field.website": defineMessage({
        defaultMessage: "Your company's website.",
    }),
};

type Props = {
    translationKey?: string | null;
    fallback: string;
};

export const LocalizedContent: React.FC<Props> = ({ translationKey, fallback }) => {
    const descriptor = translationKey ? contentMessages[translationKey] : undefined;
    if (!descriptor) return <>{fallback}</>;
    return <FormattedMessage {...descriptor} />;
};

export const hasStandardDescription = (translationKey?: string | null): boolean =>
    !!translationKey && translationKey in contentDescriptions;

export const StandardDescription: React.FC<{ translationKey?: string | null }> = ({ translationKey }) => {
    const descriptor = translationKey ? contentDescriptions[translationKey] : undefined;
    if (!descriptor) return null;
    return <FormattedMessage {...descriptor} />;
};
